import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSupportManuals } from '@/hooks/use-support-manuals';
import { ManualsModal } from './ManualsModal';
import { BookOpen, FileText, ExternalLink } from 'lucide-react';

interface ManualListProps {
  systemId?: string;
  category?: string;
  compact?: boolean;
}

const getCategoryIcon = (category: string) => {
  switch (category.toLowerCase()) {
    case 'pneus':
      return '🛞';
    case 'configuração':
      return '⚙️';
    case 'operação':
      return '🎯';
    case 'manutenção':
      return '🔧';
    case 'relatórios':
      return '📊';
    case 'integração':
      return '🔗';
    case 'troubleshooting':
      return '🔍';
    default:
      return '📄';
  }
};

export function ManualList({ systemId, category, compact = false }: ManualListProps) {
  const { data: manuals, isLoading } = useSupportManuals(systemId);
  const [manualsModalOpen, setManualsModalOpen] = useState(false);
  const [defaultManualId, setDefaultManualId] = useState<string>();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!manuals || manuals.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Nenhum manual disponível no momento.</p>
      </div>
    );
  }

  const filteredManuals = category 
    ? manuals.filter(m => m.category === category)
    : manuals;

  const displayManuals = compact ? filteredManuals.slice(0, 6) : filteredManuals;

  const openManual = (manualId: string) => {
    setDefaultManualId(manualId);
    setManualsModalOpen(true);
  };

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {displayManuals.map((manual) => (
          <Card key={manual.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="text-3xl">{getCategoryIcon(manual.category)}</div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg line-clamp-2">{manual.title}</CardTitle>
                  <CardDescription className="flex gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">
                      {manual.category}
                    </Badge>
                    {manual.systems && (
                      <Badge variant="outline" className="text-xs">
                        {manual.systems.name}
                      </Badge>
                    )}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                {manual.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => openManual(manual.id)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Abrir Manual
                </Button>
                {manual.file_path && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => window.open(manual.file_path, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {compact && filteredManuals.length > 6 && (
        <div className="text-center pt-6">
          <p className="text-sm text-muted-foreground">
            E mais {filteredManuals.length - 6} manuais disponíveis...
          </p>
        </div>
      )}

      <ManualsModal 
        isOpen={manualsModalOpen}
        onClose={() => setManualsModalOpen(false)}
        manuals={manuals || []}
        defaultManualId={defaultManualId}
      />
    </>
  );
}
